import { useCallback, useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, X, FileText, Copy } from 'lucide-react';
import { useStore } from '../../stores/useStore';
import { parseFile } from '../../lib/parser';
import { detectDuplicates } from '../../lib/analyzer';
import type { DuplicateGroup } from '../../types';
import clsx from 'clsx';

export function FileUpload() {
  const {
    setPapers,
    setCurrentFileName,
    importKeywords,
    papers,
    inclusionKeywords,
    exclusionKeywords,
    setActiveTab,
    clearKeywords,
  } = useStore();

  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keywordsFile, setKeywordsFile] = useState<{ name: string; loaded: boolean } | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{ groups: DuplicateGroup[]; total: number } | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFile = async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    // Handle keywords file
    if (extension === 'txt') {
      try {
        const text = await file.text();
        importKeywords(text);
        setKeywordsFile({ name: file.name, loaded: true });
        return;
      } catch (err) {
        setError('Failed to read keywords file');
        return;
      }
    }

    // Handle data file (CSV/Excel)
    if (extension === 'csv' || extension === 'xls' || extension === 'xlsx') {
      setLoading(true);
      setError(null);
    setDuplicateInfo(null);

      try {
        const parsedPapers = await parseFile(file);

        if (parsedPapers.length === 0) {
          throw new Error('No valid papers found in file. Make sure it has PMID and Title columns.');
        }

        // Detect duplicates
        const { uniquePapers, duplicateGroups, totalDuplicates } = detectDuplicates(parsedPapers);
        setPapers(uniquePapers);
        setDuplicateInfo({ groups: duplicateGroups, total: totalDuplicates });
        setCurrentFileName(file.name);
      } catch (err: any) {
        setError(err.message || 'Failed to parse file');
      } finally {
        setLoading(false);
      }
      return;
    }

    setError(`Unsupported file type: .${extension}`);
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      await processFile(file);
    }
  }, []);

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      await processFile(file);
    }

    e.target.value = '';
  };

  const clearPapers = () => {
    setPapers([]);
    setCurrentFileName(null);
    setError(null);
    setDuplicateInfo(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Upload Files
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Import your papers (CSV/Excel) and keywords (TXT) files
        </p>
      </div>

      {/* Main upload area */}
      <div
        className={clsx(
          'relative border-2 border-dashed rounded-2xl p-8 transition-all duration-300',
          dragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-primary-400 hover:bg-slate-50 dark:hover:bg-slate-800/50',
          loading && 'pointer-events-none opacity-50'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept=".csv,.xls,.xlsx,.txt"
          onChange={handleFileInput}
          multiple
          disabled={loading}
        />

        <div className="text-center">
          {loading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-4" />
              <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                Processing file...
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30">
                  <Upload className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
              </div>

              <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                {dragActive ? 'Drop files here' : 'Drag & drop files here'}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                or click to browse
              </p>

              <div className="flex flex-wrap justify-center gap-2">
                <span className="badge-neutral">CSV</span>
                <span className="badge-neutral">XLS</span>
                <span className="badge-neutral">XLSX</span>
                <span className="badge-neutral">TXT (Keywords)</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
          <AlertCircle className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-danger-800 dark:text-danger-200">Upload Error</p>
            <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-danger-400 hover:text-danger-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Papers status */}
        <div className={clsx(
          'card p-5 transition-all duration-300',
          papers.length > 0 && 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-slate-900'
        )}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'flex items-center justify-center w-12 h-12 rounded-xl',
                papers.length > 0
                  ? 'bg-accent-100 dark:bg-accent-900/30'
                  : 'bg-slate-100 dark:bg-slate-700'
              )}>
                <FileSpreadsheet className={clsx(
                  'w-6 h-6',
                  papers.length > 0
                    ? 'text-accent-600 dark:text-accent-400'
                    : 'text-slate-400'
                )} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Papers Data
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {papers.length > 0
                    ? `${papers.length.toLocaleString()} papers loaded`
                    : 'No file loaded'}
                </p>
              </div>
            </div>

            {papers.length > 0 && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-accent-500" />
                <button
                  onClick={clearPapers}
                  className="text-slate-400 hover:text-danger-500 transition-colors"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {papers.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">With titles</span>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {papers.filter((p) => p.title).length.toLocaleString()}
                </span>
              </div>
              {duplicateInfo && duplicateInfo.total > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <Copy className="w-3 h-3" />
                    Duplicates removed
                  </span>
                  <span className="font-medium text-amber-600 dark:text-amber-400">
                    {duplicateInfo.total.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Keywords status */}
        <div className={clsx(
          'card p-5 transition-all duration-300',
          (inclusionKeywords.length > 0 || exclusionKeywords.length > 0) &&
            'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-slate-900'
        )}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'flex items-center justify-center w-12 h-12 rounded-xl',
                (inclusionKeywords.length > 0 || exclusionKeywords.length > 0)
                  ? 'bg-accent-100 dark:bg-accent-900/30'
                  : 'bg-slate-100 dark:bg-slate-700'
              )}>
                <FileText className={clsx(
                  'w-6 h-6',
                  (inclusionKeywords.length > 0 || exclusionKeywords.length > 0)
                    ? 'text-accent-600 dark:text-accent-400'
                    : 'text-slate-400'
                )} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Keywords
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {keywordsFile
                    ? keywordsFile.name
                    : 'No keywords loaded'}
                </p>
              </div>
            </div>

            {(inclusionKeywords.length > 0 || exclusionKeywords.length > 0) && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-accent-500" />
                <button
                  onClick={() => {
                    clearKeywords();
                    setKeywordsFile(null);
                  }}
                  className="text-slate-400 hover:text-danger-500 transition-colors"
                  title="Remove keywords"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {(inclusionKeywords.length > 0 || exclusionKeywords.length > 0) && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-accent-600 dark:text-accent-400">Inclusion</span>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {inclusionKeywords.length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-danger-600 dark:text-danger-400">Exclusion</span>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {exclusionKeywords.length}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Next step button */}
      {papers.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={() => setActiveTab('keywords')}
            className="btn-primary"
          >
            Continue to Keywords
            <span className="ml-1">→</span>
          </button>
        </div>
      )}
    </div>
  );
}
